"use client";

import { useState, useMemo, useEffect, Fragment } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { ArrowUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { formatRelativeTime } from "@/lib/utils";
import { Activity } from "@/types";

interface ActivitiesTableProps {
  activities: Activity[];
  sortConfig: {
    key: "createdAt" | "action" | "module" | "description";
    direction: "asc" | "desc";
  };
  onSort: (key: "createdAt" | "action" | "module" | "description") => void;
}

const actionColors: Record<string, string> = {
  CREATE: "bg-green-50 text-green-700 border-green-100",
  UPDATE: "bg-blue-50 text-blue-700 border-blue-100",
  DELETE: "bg-red-50 text-red-700 border-red-100",
};

const moduleColors: Record<string, string> = {
  USER: "bg-purple-50 text-purple-700 border-purple-100",
  STAFF: "bg-orange-50 text-orange-700 border-orange-100",
  CUSTOMER: "bg-cyan-50 text-cyan-700 border-cyan-100",
  APPOINTMENT: "bg-indigo-50 text-indigo-700 border-indigo-100",
  SUBSCRIPTION: "bg-pink-50 text-pink-700 border-pink-100",
};

function parseActivityData(data: unknown): Record<string, unknown> | null {
  if (!data || data === "null" || data === null) return null;
  
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  
  if (typeof data === "object") {
    return data as Record<string, unknown>;
  }
  
  return null;
}

function ActivityDataView({ 
  oldData, 
  newData, 
  action 
}: { 
  oldData: unknown; 
  newData: unknown; 
  action: string;
}) {
  const parsedOldData = parseActivityData(oldData);
  const parsedNewData = parseActivityData(newData);

  if (action === "CREATE" && parsedNewData) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold text-green-700 mb-2">New Data:</div>
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(parsedNewData, null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  if (action === "DELETE" && parsedOldData) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-semibold text-red-700 mb-2">Deleted Data:</div>
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(parsedOldData, null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  if (action === "UPDATE") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-700 mb-2">Old Data:</div>
          {parsedOldData ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(parsedOldData, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-muted-foreground">
              No previous data
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-xs font-semibold text-blue-700 mb-2">New Data:</div>
          {parsedNewData ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(parsedNewData, null, 2)}
              </pre>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs text-muted-foreground">
              No new data
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

export function ActivitiesTable({
  activities,
  sortConfig,
  onSort,
}: ActivitiesTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toggleRow = (activityId: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(activityId)) {
        newSet.delete(activityId);
      } else {
        newSet.add(activityId);
      }
      return newSet;
    });
  };

  const hasData = (activity: Activity): boolean => {
    const hasOldData = Boolean(
      activity.oldData && activity.oldData !== "null" && activity.oldData !== null
    );
    const hasNewData = Boolean(
      activity.newData && activity.newData !== "null" && activity.newData !== null
    );
    return hasOldData || hasNewData;
  };

  // Calculate pagination
  const totalPages = Math.ceil(activities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedActivities = useMemo(
    () => activities.slice(startIndex, endIndex),
    [activities, startIndex, endIndex]
  );

  // Reset to page 1 when activities change
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [activities.length, currentPage, totalPages]);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near the start
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push("ellipsis");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">
            Activities ({activities.length})
          </CardTitle>
          {activities.length > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} - {Math.min(endIndex, activities.length)} of{" "}
              {activities.length}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b hover:bg-transparent">
                <TableHead className="w-10"></TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSort("action")}
                    className="h-8 px-2"
                  >
                    Action <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSort("module")}
                    className="h-8 px-2"
                  >
                    Module <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSort("description")}
                    className="h-8 px-2"
                  >
                    Description <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onSort("createdAt")}
                    className="h-8 px-2"
                  >
                    Date <ArrowUpDown className="ml-1 h-3 w-3" />
                  </Button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center text-muted-foreground py-8"
                  >
                    No activities found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedActivities.map((activity) => {
                  const isExpanded = expandedRows.has(activity.id);
                  const showExpandButton = hasData(activity);

                  return (
                    <Fragment key={activity.id}>
                      <TableRow className="border-b">
                        <TableCell className="w-10">
                          {showExpandButton && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => toggleRow(activity.id)}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-xs ${actionColors[activity.action] || "bg-gray-50 text-gray-700 border-gray-100"}`}
                          >
                            {activity.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`text-xs ${moduleColors[activity.module] || "bg-gray-50 text-gray-700 border-gray-100"}`}
                          >
                            {activity.module}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{activity.description}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatRelativeTime(new Date(activity.createdAt))}
                        </TableCell>
                      </TableRow>
                      {showExpandButton && isExpanded && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-muted/30 p-4">
                            <ActivityDataView
                              oldData={activity.oldData}
                              newData={activity.newData}
                              action={activity.action}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        {activities.length > 0 && totalPages > 1 && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                    className={
                      currentPage === 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

